import { styled } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import Box from 'shell/components/Box';

/**
 * Adds fixed, non-interactive fade overlays to the edges of a scrollable viewport.
 *
 * By default, its child owns scrolling and callers control visible fades with
 * `showTop` and `showBottom`. Set `scrollable` to let this component render and
 * observe the scroll viewport; fades then appear only when content exists beyond
 * the corresponding edge. Fade props are not forwarded to the DOM.
 *
 * @param {object} props Component props.
 * @param {React.ReactNode} props.children Viewport content.
 * @param {boolean} [props.showTop=false] Whether to show the top fade.
 * @param {boolean} [props.showBottom=false] Whether to show the bottom fade.
 * @param {boolean} [props.scrollable=false] Whether to provide scroll-aware viewport behavior.
 * @param {React.Ref} [props.scrollViewportRef] Ref assigned to the internal scroll viewport in `scrollable` mode.
 * @param {string} [props.fadeColor] Fade color; defaults to `theme.palette.ensightful.shades[0]`.
 * @param {number|string} [props.fadeHeight] Fade height; defaults to `theme.spacing(2)`.
 *
 * @example
 * <ScrollFadeContainer scrollable>
 *     <Content />
 * </ScrollFadeContainer>
 */
const ScrollFadeContainer = ({
    children,
    scrollable = false,
    scrollViewportRef: externalScrollViewportRef,
    showBottom = false,
    showTop = false,
    ...props
}) => {
    const scrollViewportRef = useRef(null);
    const [scrollEdges, setScrollEdges] = useState({ bottom: false, top: false });

    const updateScrollEdges = useCallback(() => {
        const viewport = scrollViewportRef.current;
        if (!viewport) return;

        const canScroll = viewport.scrollHeight > viewport.clientHeight;
        const top = canScroll && viewport.scrollTop > 0;
        const bottom = canScroll && viewport.scrollTop + viewport.clientHeight < viewport.scrollHeight - 1;
        setScrollEdges((current) => (current.top === top && current.bottom === bottom ? current : { top, bottom }));
    }, []);

    const setScrollViewportRef = useCallback(
        (node) => {
            scrollViewportRef.current = node;
            if (typeof externalScrollViewportRef === 'function') externalScrollViewportRef(node);
            else if (externalScrollViewportRef) externalScrollViewportRef.current = node;
        },
        [externalScrollViewportRef]
    );

    useEffect(() => {
        if (!scrollable) return undefined;

        const viewport = scrollViewportRef.current;
        if (!viewport) return undefined;

        updateScrollEdges();
        const resizeObserver = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(updateScrollEdges);
        resizeObserver?.observe(viewport);
        if (viewport.firstElementChild) resizeObserver?.observe(viewport.firstElementChild);
        window.addEventListener('resize', updateScrollEdges);

        return () => {
            resizeObserver?.disconnect();
            window.removeEventListener('resize', updateScrollEdges);
        };
    }, [scrollable, updateScrollEdges]);

    return (
        <ScrollFadeRoot
            {...props}
            showBottom={scrollable ? scrollEdges.bottom : showBottom}
            showTop={scrollable ? scrollEdges.top : showTop}
        >
            {scrollable ? (
                <ScrollViewport ref={setScrollViewportRef} onScroll={updateScrollEdges}>
                    {children}
                </ScrollViewport>
            ) : (
                children
            )}
        </ScrollFadeRoot>
    );
};

const ScrollFadeRoot = styled(Box, {
    shouldForwardProp: (prop) => !['fadeColor', 'fadeHeight', 'showBottom', 'showTop'].includes(prop),
})(({ theme, fadeColor = theme.palette.ensightful.shades[0], fadeHeight = theme.spacing(2), showBottom, showTop }) => ({
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    minWidth: 0,
    width: '100%',

    '&::before, &::after': {
        content: '""',
        position: 'absolute',
        left: 0,
        right: 0,
        height: fadeHeight,
        zIndex: 1,
        pointerEvents: 'none',
        transition: 'opacity 160ms ease',
    },

    '&::before': {
        top: 0,
        opacity: showTop ? 1 : 0,
        background: `linear-gradient(to bottom, ${fadeColor}, transparent)`,
    },

    '&::after': {
        bottom: 0,
        opacity: showBottom ? 1 : 0,
        background: `linear-gradient(to top, ${fadeColor}, transparent)`,
    },
}));

const ScrollViewport = styled(Box)({
    flex: 1,
    minHeight: 0,
    minWidth: 0,
    overflow: 'auto',
});

export default ScrollFadeContainer;
